import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchSubscription,
  subscribePro,
  startTrial,
  cancelSubscription,
  fetchPayments,
  fetchBillingInfo,
  previewTeamSubscriptionSeats,
  updateTeamSubscriptionSeats,
  type PaymentProvider,
  type BillingInfo,
  type SubscriptionWithPayments,
  type Payment,
  type PaginationMeta,
  type SeatProrationPreview,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

// --- Billing Queries ---

export const useSubscription = (token: string | null) => {
  return useQuery<SubscriptionWithPayments | null>({
    queryKey: queryKeys.billing.subscription,
    queryFn: () => fetchSubscription(token as string),
    enabled: Boolean(token),
    staleTime: 60_000,
  });
};

export const useTeamSubscription = (token: string | null, teamId: string | null) => {
  return useQuery<SubscriptionWithPayments | null>({
    queryKey: queryKeys.billing.teamSubscription(teamId ?? '__none__'),
    queryFn: () => fetchSubscription(token as string, teamId),
    enabled: Boolean(token && teamId),
    staleTime: 60_000,
  });
};

export const usePayments = (
  token: string | null,
  params: { page?: number; limit?: number; teamId?: string | null } = {},
) => {
  return useQuery<{ data: Payment[]; meta: PaginationMeta }>({
    queryKey: queryKeys.billing.payments(params),
    queryFn: () => fetchPayments(token as string, params),
    enabled: Boolean(token),
    staleTime: 60_000,
  });
};

export const useBillingInfo = (token: string | null) => {
  return useQuery<BillingInfo>({
    queryKey: queryKeys.billing.providers,
    queryFn: () => fetchBillingInfo(token as string),
    enabled: Boolean(token),
    staleTime: 300_000,
  });
};

// --- Billing Mutations ---

export const useSubscribeMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      provider: PaymentProvider;
      plan?: 'PERSONAL_PRO' | 'TEAM_PRO';
      period?: 'monthly' | 'yearly';
      teamId?: string;
      seats?: number;
      authData: Record<string, unknown>;
    }) => subscribePro(token as string, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    },
  });
};

export const useStartTrialMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input?: { plan?: 'PERSONAL_PRO' | 'TEAM_PRO'; teamId?: string; seats?: number }) =>
      startTrial(token as string, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    },
  });
};

export const useCancelSubscriptionMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input?: { teamId?: string | null }) => cancelSubscription(token as string, input?.teamId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
      if (variables?.teamId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.billing.teamSubscription(variables.teamId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      }
    },
  });
};

export const usePreviewTeamSubscriptionSeatsMutation = (token: string | null) => {
  return useMutation<SeatProrationPreview, Error, { teamId: string; seats: number }>({
    mutationFn: ({ teamId, seats }) => previewTeamSubscriptionSeats(token as string, teamId, seats),
  });
};

export const useUpdateTeamSubscriptionSeatsMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, seats }: { teamId: string; seats: number }) =>
      updateTeamSubscriptionSeats(token as string, teamId, seats),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.billing.teamSubscription(variables.teamId) });
    },
  });
};
